import { Link } from '@tanstack/react-router'
import { Lock } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

interface AccessDeniedStateProps {
  /** `page` for a whole route/detail view, `section` for a tab or panel inside a readable page. */
  variant: 'page' | 'section'
  /**
   * Plural, user-vocabulary domain noun ("invoices", "team settings").
   * Falls back to "this page" / "this section". Never an entity name, ID, or
   * permission key — see the information-leak boundary in the permission UX design.
   */
  noun?: string
  className?: string
}

/**
 * "You can't see this" state for RBAC denials. Deliberately reads as *locked,
 * not broken*: a muted lock, no destructive colour, no warning triangle, no
 * "contact support" — the block is resolvable inside the org by an admin.
 *
 * Renders no counts, no record data and no permission identifiers, so it is
 * safe on any surface in the tenant app regardless of what was hidden.
 */
export function AccessDeniedState({ variant, noun, className }: AccessDeniedStateProps) {
  const subject = noun || (variant === 'page' ? 'this page' : 'this section')

  if (variant === 'section') {
    return (
      <div
        data-slot="access-denied-section"
        className={cn(
          'flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed p-6 text-center',
          className
        )}
      >
        <Lock className="size-5 text-muted-foreground" aria-hidden="true" />
        <p className="text-sm font-medium">You don't have access to {subject}.</p>
        <p className="text-xs text-muted-foreground">
          Ask an admin on your team to update your role.
        </p>
      </div>
    )
  }

  return (
    <div
      data-slot="access-denied-page"
      className={cn('flex flex-1 items-center justify-center p-6', className)}
    >
      <div className="flex flex-col items-center gap-6 text-center">
        <div className="flex h-24 w-24 items-center justify-center rounded-full bg-muted">
          <Lock className="h-10 w-10 text-muted-foreground" aria-hidden="true" />
        </div>
        <div className="space-y-2">
          <h2 className="text-xl font-medium">You don't have access to {subject}</h2>
          <p className="max-w-md text-muted-foreground">
            Your role doesn't include access to {subject}. If you need it, ask an admin on your
            team to update your role.
          </p>
        </div>
        <Button asChild>
          <Link to="/dashboard">Back to Dashboard</Link>
        </Button>
      </div>
    </div>
  )
}
